// The quiz's pure navigation and conclusion rules — ported from
// after-www's BuildAQuote handleAnswer/handleBack exactly, restated
// against this port's data shape. Nothing here touches the network,
// localStorage or React; every screen and the checkpoint sync build on
// these functions rather than re-deriving the navigation themselves.

import { QUIZ_QUESTIONS } from "./questions";
import {
  isPreplanTiming,
  type Answers,
  type Question,
  type QuizResolution,
} from "./types";

/** `nextScreen`'s result once nothing follows the last question on the
 * path actually answered. */
export const QUIZ_COMPLETE = "complete";

export type EngineDestination = string | typeof QUIZ_COMPLETE;

function shouldShow(question: Question, answers: Answers): boolean {
  if (!question.showWhen) return true;
  return Object.entries(question.showWhen).every(
    ([key, value]) => answers[key] === value,
  );
}

/**
 * Where answering `currentKey` with `answer` goes next. Mirrors
 * after-www's `handleAnswer` in order: a matching `conditionalNext` wins
 * outright (bypassing `showWhen` entirely), then a response screen's own
 * `nextStep`, and only then the sequential order — which itself skips any
 * entry whose `showWhen` the answer set (with `answer` folded in) does
 * not satisfy. Past the last entry answers `QUIZ_COMPLETE`.
 */
export function nextScreen(
  questions: readonly Question[],
  currentKey: string,
  answers: Answers,
  answer: string | string[],
): EngineDestination {
  const index = questions.findIndex((q) => q.key === currentKey);
  if (index === -1) return QUIZ_COMPLETE;
  const question = questions[index];
  const updatedAnswers: Answers = { ...answers, [currentKey]: answer };

  if (question.conditionalNext && typeof answer === "string") {
    const target = question.conditionalNext[answer];
    if (target && questions.some((q) => q.key === target)) return target;
  }
  if (question.type === "response" && question.nextStep) {
    if (questions.some((q) => q.key === question.nextStep)) {
      return question.nextStep;
    }
  }

  for (let next = index + 1; next < questions.length; next++) {
    if (shouldShow(questions[next], updatedAnswers)) return questions[next].key;
  }
  return QUIZ_COMPLETE;
}

/**
 * Where Back from `currentKey` lands — the previous entry that is neither
 * a response screen (informational, not a decision to revisit) nor
 * hidden by its own `showWhen`. Mirrors after-www's `handleBack`. Null at
 * (or before) the first entry.
 */
export function previousScreen(
  questions: readonly Question[],
  currentKey: string,
  answers: Answers,
): string | null {
  const index = questions.findIndex((q) => q.key === currentKey);
  if (index <= 0) return null;
  let previous = index - 1;
  while (previous >= 0) {
    const candidate = questions[previous];
    if (candidate.type === "response") {
      previous--;
      continue;
    }
    if (!shouldShow(candidate, answers)) {
      previous--;
      continue;
    }
    break;
  }
  return previous >= 0 ? questions[previous].key : null;
}

/** Traces the path the given answers actually describe, from the first
 * entry, following the same rules `nextScreen` would apply at each step —
 * response screens auto-advance ("continue" is not itself an answer).
 * `complete` is true only once the path runs off the end with no gap. */
function tracePath(
  questions: readonly Question[],
  answers: Answers,
): { complete: boolean } {
  let key: string | undefined = questions[0]?.key;
  const visited = new Set<string>();
  while (key !== undefined) {
    if (visited.has(key)) return { complete: false };
    visited.add(key);
    const question = questions.find((q) => q.key === key);
    if (!question) return { complete: false };
    const answer = question.type === "response" ? "continue" : answers[key];
    if (answer === undefined) return { complete: false };
    const destination = nextScreen(questions, key, answers, answer);
    key = destination === QUIZ_COMPLETE ? undefined : destination;
  }
  return { complete: true };
}

/** Whether every question on the path these answers describe — the
 * questions the conditional/sequential rules actually route through, not
 * the full list — has an answer, through the path's last entry. */
export function pathIsComplete(answers: Answers): boolean {
  return tracePath(QUIZ_QUESTIONS, answers).complete;
}

/**
 * The checkpoint conclusion these answers currently describe, or null
 * while neither terminal state has been reached yet. `hospice === "yes"`
 * wins outright regardless of anything else on the path (reachable or
 * not) — the dead end is a fact about the answer, not about where the
 * quiz currently stands.
 */
export function deriveConclusion(answers: Answers): QuizResolution | null {
  if (answers.hospice === "yes") return { conclusion: "at_need_imminent" };
  if (!pathIsComplete(answers)) return null;
  const timing = answers.preplanTiming;
  if (!isPreplanTiming(timing)) return null;
  return { conclusion: "completed", preplan_timing: timing };
}

/** The screen a fresh (or resuming) arrival opens on: the dead end when
 * the server's standing checkpoint already says so — even with no local
 * answers on this device — else whatever screen local storage says the
 * family was on, else the quiz's first entry. */
export function initialQuizScreen(args: {
  storedScreen: string | null;
  serverConclusion: QuizResolution | null;
}): string {
  if (args.serverConclusion?.conclusion === "at_need_imminent") {
    return slugFor("urgent_support");
  }
  if (args.storedScreen && questionBySlug(args.storedScreen)) {
    return args.storedScreen;
  }
  return QUIZ_QUESTIONS[0].slug;
}

/** The URL segment for an entry's key; an unknown key falls back to the
 * first screen rather than minting a segment nothing routes. */
export function slugFor(key: string): string {
  return (
    QUIZ_QUESTIONS.find((q) => q.key === key)?.slug ?? QUIZ_QUESTIONS[0].slug
  );
}

export function questionBySlug(slug: string): Question | null {
  return QUIZ_QUESTIONS.find((q) => q.slug === slug) ?? null;
}
